<template>
  <b-modal
    :visible="visible"
    size="lg"
    centered
    scrollable
    hide-footer
    modal-class="source-detail-modal"
    @change="$emit('update:visible', $event)"
  >
    <template #modal-header="{ close }">
      <div
        class="sd-header"
        :style="{ '--accent': theme.color, '--accent-soft': soft }"
      >
        <div class="sd-header__icon">
          <feather-icon
            :icon="theme.icon"
            size="24"
          />
        </div>
        <div class="sd-header__text">
          <h4 class="sd-header__title">
            {{ theme.label }}
          </h4>
          <span class="sd-header__subtitle">{{ subtitle }}</span>
        </div>
        <button
          type="button"
          class="sd-header__close"
          aria-label="Close"
          @click="close()"
        >
          <feather-icon
            icon="XIcon"
            size="18"
          />
        </button>
      </div>
    </template>

    <div
      class="sd-body"
      :style="{ '--accent': theme.color, '--accent-soft': soft }"
    >
      <!-- core numbers -->
      <div class="sd-tiles">
        <div
          v-for="metric in parts.core"
          :key="metric.key"
          class="sd-tile"
          :style="{ '--tile': metric.color, '--tile-soft': tintOf(metric.color) }"
        >
          <span class="sd-tile__icon">
            <feather-icon
              :icon="metric.icon"
              size="16"
            />
          </span>
          <span class="sd-tile__value">{{ format(metric.value) }}</span>
          <span class="sd-tile__label">{{ metric.label }}</span>
        </div>
      </div>

      <!-- source specific statuses -->
      <div
        v-if="parts.breakdown.length"
        class="sd-breakdown"
      >
        <h6 class="sd-breakdown__title">
          <feather-icon
            icon="ListIcon"
            size="16"
          />
          Status breakdown
        </h6>

        <div
          v-for="item in parts.breakdown"
          :key="item.key"
          class="sd-row"
        >
          <div class="sd-row__head">
            <span class="sd-row__label">{{ item.label }}</span>
            <span class="sd-row__value">{{ format(item.value) }}</span>
          </div>
          <div class="sd-row__track">
            <div
              class="sd-row__bar"
              :style="{ width: barWidth(item.value) }"
            />
          </div>
        </div>
      </div>

      <div
        v-else
        class="sd-empty"
      >
        <feather-icon
          icon="InboxIcon"
          size="28"
        />
        <span>No extra status details for this source.</span>
      </div>
    </div>
  </b-modal>
</template>

<script>
import {
  getSourceTheme, splitAnalysis, tint, formatNumber,
} from './analysisTheme'

export default {
  props: {
    visible: { type: Boolean, default: false },
    source: { type: String, default: '' },
    subtitle: { type: String, default: '' },
    data: { type: Object, default: () => ({}) },
  },
  computed: {
    theme() {
      return getSourceTheme(this.source)
    },
    soft() {
      return tint(this.theme.color, 0.14)
    },
    parts() {
      return splitAnalysis(this.data || {})
    },
    maxValue() {
      return Math.max(1, ...this.parts.breakdown.map(i => i.value))
    },
  },
  methods: {
    format: formatNumber,
    tintOf(color) {
      return tint(color, 0.12)
    },
    barWidth(value) {
      return `${Math.max((value / this.maxValue) * 100, value > 0 ? 3 : 0)}%`
    },
  },
}
</script>

<style lang="scss">
.source-detail-modal {
  .modal-header {
    padding: 0;
    border-bottom: 0;
    display: block;
  }
  .modal-content {
    border: 0;
    border-radius: 18px;
    overflow: hidden;
  }
  .modal-body {
    padding: 0;
    background: #f8f8f8;
  }
}

.sd-header {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 20px 24px;
  color: #fff;
  background: linear-gradient(120deg, var(--accent), #1d2b44 220%);

  &__icon {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 50px;
    height: 50px;
    border-radius: 14px;
    background: rgba(255, 255, 255, 0.2);
  }

  &__text {
    display: flex;
    flex-direction: column;
    flex: 1;
    min-width: 0;
  }

  &__title {
    margin: 0;
    color: #fff;
    font-weight: 700;
  }

  &__subtitle {
    font-size: 0.85rem;
    opacity: 0.85;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  &__close {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 34px;
    height: 34px;
    border: 0;
    border-radius: 50%;
    color: #fff;
    background: rgba(255, 255, 255, 0.18);
    cursor: pointer;
    transition: background 0.2s;

    &:hover {
      background: rgba(255, 255, 255, 0.32);
    }
  }
}

.sd-body {
  padding: 22px 24px 26px;
}

.sd-tiles {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(135px, 1fr));
  gap: 12px;
  margin-bottom: 22px;
}

.sd-tile {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 2px;
  padding: 12px 14px;
  background: #eef4fb;
  border: 1px solid #d5e2f2;
  border-radius: 12px;

  &__icon {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 30px;
    height: 30px;
    margin-bottom: 4px;
    border-radius: 9px;
    color: var(--tile);
    background: var(--tile-soft);
  }

  &__value {
    font-size: 1.35rem;
    font-weight: 700;
    line-height: 1.2;
    color: #1d2b44;
  }

  &__label {
    font-size: 0.74rem;
    font-weight: 600;
    letter-spacing: 0.03em;
    text-transform: uppercase;
    color: #6b7a90;
  }
}

.sd-breakdown {
  padding: 18px 20px 8px;
  background: #eef4fb;
  border: 1px solid #d5e2f2;
  border-radius: 14px;

  &__title {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 14px;
    font-weight: 700;
    color: var(--accent);
  }
}

.sd-row {
  margin-bottom: 14px;

  &__head {
    display: flex;
    justify-content: space-between;
    gap: 12px;
    margin-bottom: 5px;
    font-size: 0.86rem;
  }

  &__label {
    color: #44546a;
  }

  &__value {
    font-weight: 700;
    color: #1d2b44;
  }

  &__track {
    height: 8px;
    border-radius: 99px;
    background: var(--accent-soft);
    overflow: hidden;
  }

  &__bar {
    height: 100%;
    border-radius: 99px;
    background: var(--accent);
    transition: width 0.6s ease;
  }
}

.sd-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  padding: 26px 0;
  color: #6b7a90;
}
</style>
